import React, { useState, useEffect } from "react";
import { Plus, X } from "lucide-react";

export default function TourForm({
  formData,
  setFormData,
  thumbnailPreview,
  setThumbnailPreview,
  imagePreviews,
  setImagePreviews,
  handleInputChange,
  handleThumbnailChange,
  handleImagesChange,
  removeImage,
  handleSubmit,
  editingTourId,
  cancelEditing,
  isLoading,
}) {
  // useEffect(() => {
  //   console.log("Form Data:", formData);
  // }, [formData]);
  // État local pour gérer les jours de l'itinéraire
  const [itineraryDays, setItineraryDays] = useState([]);
  const [numberOfDays, setNumberOfDays] = useState(0);

  // Initialiser l'itinéraire quand on édite un tour
  useEffect(() => {
    if (Array.isArray(formData.itinerary) && formData.itinerary.length > 0) {
      // ✅ Format JSON uniquement: [{day: 1, description: "..."}, ...]
      setItineraryDays(formData.itinerary);
      setNumberOfDays(formData.itinerary.length);
    } else {
      // ✅ Pas d'itinéraire ou null/vide
      setItineraryDays([]);
      setNumberOfDays(0);
    }
  }, [editingTourId, formData.itinerary]);

  // Mettre à jour le nombre de jours
  const handleNumberOfDaysChange = (value) => {
    const num = parseInt(value) || 0;
    setNumberOfDays(num);

    // Créer/ajuster le tableau des jours
    const newDays = [];
    for (let i = 0; i < num; i++) {
      newDays.push(itineraryDays[i] || { day: i + 1, title: "", description: "", highlights: [] });
    }
    setItineraryDays(newDays);

    // ✅ Mettre à jour formData directement avec l'array JSON
    handleInputChange("itinerary", newDays);
  };

  // Mettre à jour un champ d'un jour d'itinéraire
  const handleDayFieldChange = (index, field, value) => {
    if (itineraryDays[index]?.[field] === value) return;

    const newDays = [...itineraryDays];
    newDays[index] = { ...newDays[index], [field]: value };
    setItineraryDays(newDays);
    handleInputChange("itinerary", newDays);
  };

  // Raccourci pour la description (rétrocompatibilité)
  const handleDayDescriptionChange = (index, value) => {
    handleDayFieldChange(index, "description", value);
  };

  // Ajouter un highlight à un jour
  const addHighlight = (index, highlight) => {
    if (!highlight.trim()) return;
    const newDays = [...itineraryDays];
    const currentHighlights = Array.isArray(newDays[index].highlights) ? newDays[index].highlights : [];
    if (currentHighlights.includes(highlight.trim())) return; // avoid duplicates
    newDays[index] = { ...newDays[index], highlights: [...currentHighlights, highlight.trim()] };
    setItineraryDays(newDays);
    handleInputChange("itinerary", newDays);
  };

  // Supprimer un highlight d'un jour
  const removeHighlight = (dayIndex, highlightIndex) => {
    const newDays = [...itineraryDays];
    const currentHighlights = [...(newDays[dayIndex].highlights || [])];
    currentHighlights.splice(highlightIndex, 1);
    newDays[dayIndex] = { ...newDays[dayIndex], highlights: currentHighlights };
    setItineraryDays(newDays);
    handleInputChange("itinerary", newDays);
  };

  // Preset highlight options
  const highlightPresets = ["Breakfast", "Lunch", "Dinner", "Hotel", "Transport", "Guide", "Camel Ride", "Sandboarding", "Campfire", "Stargazing", "Swimming Pool"];

  return (
    <div className="rounded-xl border border-slate-200 p-6 dark:border-slate-800">
      <h3 className="mb-4 text-lg font-semibold">
        {editingTourId ? "Modifier le circuit" : "Ajouter un nouveau circuit"}
      </h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Title *
          </label>
          <input
            value={formData.title}
            onChange={(e) => handleInputChange("title", e.target.value)}
            placeholder="Ex: Fès Impériale"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            City *
          </label>
          <input
            value={formData.city}
            onChange={(e) => handleInputChange("city", e.target.value)}
            placeholder="Ex: Fès"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Duration (days) *
          </label>
          <input
            type="number"
            value={formData.duration_days}
            onChange={(e) => handleInputChange("duration_days", e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="Ex: 2"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Start Date
          </label>
          <input
            type="date"
            value={formData.start_date || ""}
            onChange={(e) => handleInputChange("start_date", e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Fixed departure date for this tour (optional)
          </p>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Price (DH) *
          </label>
          <input
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => handleInputChange("price", e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="Ex: 210.50"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Note *
          </label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="5"
            value={formData.rating}
            onChange={(e) => handleInputChange("rating", e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="Ex: 4.4"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Principale Image *
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleThumbnailChange}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
          {thumbnailPreview && (
            <div className="mt-2">
              <img
                src={thumbnailPreview}
                alt="Thumbnail preview"
                className="h-20 w-20 rounded-md object-cover"
              />
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Images Secondaires
          </label>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImagesChange}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
          {imagePreviews.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="relative">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="h-20 w-20 rounded-md object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Abstract *
          </label>
          <textarea
            value={formData.summary}
            onChange={(e) => handleInputChange("summary", e.target.value)}
            placeholder="Ex: Medersa, tanneries, artisanat et gastronomie fassie."
            rows="3"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>

        {/* Nouvelle section pour l'itinéraire dynamique */}
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            How many days in the itinerary?
          </label>
          <input
            type="number"
            min="0"
            max="30"
            value={numberOfDays}
            onChange={(e) => handleNumberOfDaysChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="Ex: 3"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
        </div>

        {/* Afficher les champs pour chaque jour */}
        {itineraryDays.length > 0 && (
          <div className="md:col-span-2 space-y-4">
            <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Itinerary Details
            </h4>
            {itineraryDays.map((day, index) => (
              <div
                key={index}
                className="rounded-lg border border-slate-200 p-4 dark:border-slate-700 space-y-3"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                    {day.day}
                  </span>
                  <input
                    value={day.title || ""}
                    onChange={(e) => handleDayFieldChange(index, "title", e.target.value)}
                    placeholder={`Day ${day.day} title (e.g. Marrakech to Ait Ben Haddou)`}
                    className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  />
                </div>
                <textarea
                  value={day.description || ""}
                  onChange={(e) =>
                    handleDayDescriptionChange(index, e.target.value)
                  }
                  placeholder={`Describe the day's activities...`}
                  rows="2"
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
                {/* Highlights section */}
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-500 dark:text-slate-400">
                    Highlights / Inclusions
                  </label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(Array.isArray(day.highlights) ? day.highlights : []).map((hl, hlIdx) => (
                      <span
                        key={hlIdx}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                      >
                        {hl}
                        <button
                          type="button"
                          onClick={() => removeHighlight(index, hlIdx)}
                          className="ml-0.5 rounded-full p-0.5 hover:bg-emerald-200 dark:hover:bg-emerald-800"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {highlightPresets
                      .filter((p) => !(Array.isArray(day.highlights) ? day.highlights : []).includes(p))
                      .map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => addHighlight(index, preset)}
                          className="inline-flex items-center gap-0.5 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-xs text-slate-500 hover:border-emerald-400 hover:text-emerald-600 dark:border-slate-600 dark:text-slate-400 dark:hover:border-emerald-500 dark:hover:text-emerald-400 transition-colors"
                        >
                          <Plus size={10} /> {preset}
                        </button>
                      ))}
                  </div>
                  {/* Custom highlight input */}
                  <div className="mt-2 flex gap-2">
                    <input
                      id={`custom-hl-${index}`}
                      placeholder="Custom highlight..."
                      className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addHighlight(index, e.target.value);
                          e.target.value = "";
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById(`custom-hl-${index}`);
                        addHighlight(index, input.value);
                        input.value = "";
                      }}
                      className="rounded-md bg-emerald-600 px-3 py-1 text-xs text-white hover:bg-emerald-700"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end gap-2">
        {editingTourId ? (
          <>
            <button
              onClick={cancelEditing}
              className="rounded-md bg-slate-400 px-6 py-2 text-sm text-white hover:bg-slate-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="rounded-md bg-blue-600 px-6 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Updating..." : "Update Tour"}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={cancelEditing}
              className="rounded-md bg-slate-400 px-6 py-2 text-sm text-white hover:bg-slate-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="rounded-md bg-emerald-600 px-6 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {isLoading ? "Adding..." : "Add Tour"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
